import React, { useMemo, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Building2, Database, FileText, KeyRound, LayoutGrid, Palette, Search, Settings, Shield, Users } from "lucide-react";
import { AN_COLORS, AN_FONT } from "../../design-system/tokens";
import { getMfaMode } from "../../config/sidebarControls/mode/mfa";
import { getDomainUrl } from "../../utils/helpers";

// Settings, laid out like a browser's settings: its own menu on the left, the
// chosen page on the right. Shared by every MFA mode and opened from the
// account menu, so the product sidebar is not shown here (see layout.jsx).

export const SETTINGS_PAGES = [
  { to: "/pam/settings", end: true, label: "Overview", Icon: LayoutGrid, keywords: "account profile home" },
  { to: "/pam/settings/users", label: "Users & Access", Icon: Users, keywords: "accounts roles admins people invite" },
  { to: "/pam/settings/mfaProvider", label: "MFA Provider", Icon: Shield, keywords: "push duo okta authenticator phones devices invites campaign approve app activity number matching" },
  { to: "/pam/settings/tenantConfig", label: "Tenant Configuration", Icon: Settings, keywords: "my mfa factors enforcement security defaults" },
  { to: "/pam/settings/logsConfig", label: "Logs Configuration", Icon: FileText, keywords: "siem syslog splunk elasticsearch http forward" },
  { to: "/pam/settings/recordingStorage", label: "Recording Storage", Icon: Database, keywords: "s3 gcs bucket recordings ssh sessions" },
  { to: "/pam/settings/sso", label: "Single sign-on", Icon: KeyRound, keywords: "sso github oidc google okta entra" },
  { to: "/pam/settings/appearance", label: "Appearance", Icon: Palette, keywords: "theme dark mode light system colour color" },
];

export default function SettingsShell() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [query, setQuery] = useState("");
  const mode = getMfaMode(localStorage.getItem("currentMode"));
  const tenantHost = (getDomainUrl() || "").replace(/^https?:\/\//, "").replace(/\/.*$/, "");

  const q = query.trim().toLowerCase();
  const pages = useMemo(
    () => (q ? SETTINGS_PAGES.filter((p) => `${p.label} ${p.keywords}`.toLowerCase().includes(q)) : SETTINGS_PAGES),
    [q],
  );

  return (
    <div
      style={{
        width: "calc(100% + 24px)",
        height: "calc(100% + 48px)",
        margin: "-24px -12px",
        display: "flex",
        background: AN_COLORS.bgWarm,
        fontFamily: AN_FONT,
        color: AN_COLORS.ink,
        overflow: "hidden",
      }}
    >
      {/* Settings menu */}
      <aside
        style={{
          width: 280,
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          padding: "26px 16px 18px",
          borderRight: `1px solid ${AN_COLORS.line}`,
          background: AN_COLORS.surface,
          overflowY: "auto",
        }}
      >
        <h1 style={{ margin: "0 8px 16px", fontSize: 22, fontWeight: 600, letterSpacing: -0.4 }}>Settings</h1>
        <label className="an-settings-search">
          <Search size={15} color={AN_COLORS.muted} style={{ flexShrink: 0 }} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && pages[0] && pages[0].to !== pathname) navigate(pages[0].to);
              if (e.key === "Escape") setQuery("");
            }}
            placeholder="Search settings"
            aria-label="Search settings"
          />
        </label>

        <nav style={{ display: "grid", gap: 2 }}>
          {pages.map(({ to, end, label, Icon }) => (
            <NavLink key={to} to={to} end={end} className="an-settings-nav" style={{ textDecoration: "none" }}>
              {({ isActive }) => (
                <span
                  style={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "9px 12px",
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? AN_COLORS.ink : AN_COLORS.ink2,
                    background: isActive ? AN_COLORS.primaryL : "transparent",
                  }}
                >
                  {isActive && (
                    <span
                      style={{ position: "absolute", left: 0, top: 8, bottom: 8, width: 3, borderRadius: 3, background: AN_COLORS.primary }}
                    />
                  )}
                  <Icon size={17} color={isActive ? AN_COLORS.primary : AN_COLORS.ink2} />
                  {label}
                </span>
              )}
            </NavLink>
          ))}
          {pages.length === 0 && (
            <span style={{ padding: "8px 12px", fontSize: 13, color: AN_COLORS.muted }}>No settings match “{query}”.</span>
          )}
        </nav>

        <div style={{ marginTop: "auto", paddingTop: 18, display: "grid", gap: 12 }}>
          <button
            type="button"
            onClick={() => navigate(mode?.dashboardRoute || "/")}
            className="an-settings-back"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              padding: "9px 12px",
              border: `1px solid ${AN_COLORS.line}`,
              borderRadius: 8,
              background: AN_COLORS.surface,
              color: AN_COLORS.ink,
              fontSize: 13.5,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: AN_FONT,
            }}
          >
            <ArrowLeft size={15} /> Back to {mode?.label || "the console"}
          </button>
          {tenantHost && (
            <div style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "0 8px", fontSize: 12, color: AN_COLORS.muted, lineHeight: 1.45 }}>
              <Building2 size={14} style={{ flexShrink: 0, marginTop: 2 }} />
              <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>
                <span style={{ display: "block", fontWeight: 600, color: AN_COLORS.ink2 }}>Managed for your organisation</span>
                {tenantHost}
              </span>
            </div>
          )}
        </div>
      </aside>

      {/* The chosen page */}
      <section style={{ flex: 1, minWidth: 0, overflowY: "auto" }}>
        <div style={{ maxWidth: 1120, padding: "26px clamp(20px, 4vw, 56px) 32px" }}>
          <Outlet />
        </div>
      </section>
    </div>
  );
}
